/**
 * 任意角度の直方体（OBB）と、プレイヤー AABB との SAT 交差・スイープ。
 * three.js には依存しない。
 */

import type { BlockPose } from './block-lattice';
import { braceCrossSectionBasis, braceExtrudeOffset, braceSegments, planeSlab, vec } from './geometry';
import type { BraceGeometry, PlaneGeometry, Vec3 } from './types';
import { computePolygonNormal } from './validation';
import { localToWorldXZ } from './view-grid';

export interface AabbLike {
  min: Vec3;
  max: Vec3;
}

export interface Obb {
  center: Vec3;
  half: Vec3;
  axes: [Vec3, Vec3, Vec3];
}

export interface SweepHit {
  /** 変位に対する衝突時刻（0–1）。0 は今いる位置ですでに接触。 */
  fraction: number;
  /** 障害物から AABB へ向かう単位法線 */
  normal: Vec3;
}

export interface Mtv {
  offset: Vec3;
  normal: Vec3;
  depth: number;
}

const WORLD_AXES: [Vec3, Vec3, Vec3] = [
  { x: 1, y: 0, z: 0 },
  { x: 0, y: 1, z: 0 },
  { x: 0, y: 0, z: 1 },
];

const AXIS_EPS = 1e-8;
const RECT_LEN_EPS = 1e-5;
const RECT_DOT_EPS = 1e-4;

export function isWorldAligned(v: Vec3, epsilon = 1e-6): boolean {
  let zeros = 0;
  if (Math.abs(v.x) < epsilon) zeros++;
  if (Math.abs(v.y) < epsilon) zeros++;
  if (Math.abs(v.z) < epsilon) zeros++;
  return zeros >= 2;
}

export function obbIsWorldAligned(obb: Obb): boolean {
  return (
    isWorldAligned(obb.axes[0]) && isWorldAligned(obb.axes[1]) && isWorldAligned(obb.axes[2])
  );
}

export function aabbCenter(box: AabbLike): Vec3 {
  return {
    x: (box.min.x + box.max.x) * 0.5,
    y: (box.min.y + box.max.y) * 0.5,
    z: (box.min.z + box.max.z) * 0.5,
  };
}

export function aabbHalf(box: AabbLike): Vec3 {
  return {
    x: (box.max.x - box.min.x) * 0.5,
    y: (box.max.y - box.min.y) * 0.5,
    z: (box.max.z - box.min.z) * 0.5,
  };
}

export function obbBounds(obb: Obb): AabbLike {
  const r = {
    x:
      obb.half.x * Math.abs(obb.axes[0].x) +
      obb.half.y * Math.abs(obb.axes[1].x) +
      obb.half.z * Math.abs(obb.axes[2].x),
    y:
      obb.half.x * Math.abs(obb.axes[0].y) +
      obb.half.y * Math.abs(obb.axes[1].y) +
      obb.half.z * Math.abs(obb.axes[2].y),
    z:
      obb.half.x * Math.abs(obb.axes[0].z) +
      obb.half.y * Math.abs(obb.axes[1].z) +
      obb.half.z * Math.abs(obb.axes[2].z),
  };
  return {
    min: { x: obb.center.x - r.x, y: obb.center.y - r.y, z: obb.center.z - r.z },
    max: { x: obb.center.x + r.x, y: obb.center.y + r.y, z: obb.center.z + r.z },
  };
}

export function blockObb(geometry: BlockPose): Obb {
  const { origin, size, yaw } = geometry;
  const xAxis = localToWorldXZ(1, 0, yaw);
  const zAxis = localToWorldXZ(0, 1, yaw);
  return {
    center: { ...origin },
    half: { x: size.x / 2, y: size.y / 2, z: size.z / 2 },
    axes: [
      { x: xAxis.x, y: 0, z: xAxis.z },
      { x: 0, y: 1, z: 0 },
      { x: zAxis.x, y: 0, z: zAxis.z },
    ],
  };
}

export function braceObb(geometry: BraceGeometry): Obb | null {
  return braceObbs(geometry)[0] ?? null;
}

/** 折れ線の辺ごとに 1 つの OBB。マイターの食い込みは見ない。押し出し中は空。 */
export function braceObbs(geometry: BraceGeometry): Obb[] {
  if (geometry.revolve) return [];
  if (vec.length(braceExtrudeOffset(geometry)) >= 1e-9) return [];
  const obbs: Obb[] = [];
  for (const segment of braceSegments(geometry)) {
    const obb = braceSegmentObb(segment.start, segment.end, geometry);
    if (obb) obbs.push(obb);
  }
  return obbs;
}

function braceSegmentObb(
  start: { x: number; y: number; z: number },
  end: { x: number; y: number; z: number },
  geometry: BraceGeometry
): Obb | null {
  const axisVec = vec.sub(end, start);
  const length = vec.length(axisVec);
  if (length < 1e-9) return null;
  const { axis, right, up } = braceCrossSectionBasis(axisVec, geometry.rotation ?? 0);
  return {
    center: vec.scale(vec.add(start, end), 0.5),
    half: {
      x: length / 2,
      y: geometry.thickness / 2,
      z: geometry.thickness / 2,
    },
    axes: [axis, right, up],
  };
}

/** 三角形を厚み方向へ伸ばした三角柱。描画と同じ三角形を衝突に載せる。 */
export interface TrianglePrism {
  a: Vec3;
  b: Vec3;
  c: Vec3;
  offset: Vec3;
}

export function prismVertices(prism: TrianglePrism): [Vec3, Vec3, Vec3, Vec3, Vec3, Vec3] {
  return [
    prism.a,
    prism.b,
    prism.c,
    vec.add(prism.a, prism.offset),
    vec.add(prism.b, prism.offset),
    vec.add(prism.c, prism.offset),
  ];
}

export function prismCentroid(prism: TrianglePrism): Vec3 {
  const verts = prismVertices(prism);
  return {
    x: (verts[0].x + verts[1].x + verts[2].x + verts[3].x + verts[4].x + verts[5].x) / 6,
    y: (verts[0].y + verts[1].y + verts[2].y + verts[3].y + verts[4].y + verts[5].y) / 6,
    z: (verts[0].z + verts[1].z + verts[2].z + verts[3].z + verts[4].z + verts[5].z) / 6,
  };
}

export function prismBounds(prism: TrianglePrism): AabbLike {
  const verts = prismVertices(prism);
  const min = { x: verts[0].x, y: verts[0].y, z: verts[0].z };
  const max = { x: verts[0].x, y: verts[0].y, z: verts[0].z };
  for (let i = 1; i < verts.length; i++) {
    const p = verts[i];
    if (p.x < min.x) min.x = p.x;
    if (p.y < min.y) min.y = p.y;
    if (p.z < min.z) min.z = p.z;
    if (p.x > max.x) max.x = p.x;
    if (p.y > max.y) max.y = p.y;
    if (p.z > max.z) max.z = p.z;
  }
  return { min, max };
}

/** 4 点の長方形プレーンなら OBB。それ以外は null。 */
export function rectanglePlaneObb(geometry: PlaneGeometry): Obb | null {
  if (geometry.arcs && geometry.arcs.length > 0) return null;
  if (geometry.holes && geometry.holes.length > 0) return null;
  if (geometry.sideHoles && geometry.sideHoles.length > 0) return null;
  const { points } = geometry;
  if (points.length !== 4) return null;
  const slab = planeSlab(geometry);
  if (!slab) return null;
  const rawNormal = geometry.normal ?? computePolygonNormal(points);
  if (!rawNormal) return null;
  const normal = vec.normalize(rawNormal);
  if (vec.length(normal) < 1e-9) return null;

  const edges = [
    vec.sub(points[1], points[0]),
    vec.sub(points[2], points[1]),
    vec.sub(points[3], points[2]),
    vec.sub(points[0], points[3]),
  ];
  const lengths = edges.map((edge) => vec.length(edge));
  if (lengths.some((len) => len < 1e-9)) return null;
  if (Math.abs(lengths[0] - lengths[2]) > RECT_LEN_EPS) return null;
  if (Math.abs(lengths[1] - lengths[3]) > RECT_LEN_EPS) return null;

  const axis0 = vec.scale(edges[0], 1 / lengths[0]);
  const axis1 = vec.scale(edges[1], 1 / lengths[1]);
  if (Math.abs(vec.dot(axis0, axis1)) > RECT_DOT_EPS) return null;

  let axis2 = vec.normalize(vec.cross(axis0, axis1));
  if (vec.length(axis2) < 1e-9) return null;
  if (vec.dot(axis2, normal) < 0) axis2 = vec.scale(axis2, -1);

  const startCenter = {
    x: (slab.start[0].x + slab.start[1].x + slab.start[2].x + slab.start[3].x) / 4,
    y: (slab.start[0].y + slab.start[1].y + slab.start[2].y + slab.start[3].y) / 4,
    z: (slab.start[0].z + slab.start[1].z + slab.start[2].z + slab.start[3].z) / 4,
  };

  return {
    center: vec.add(startCenter, vec.scale(slab.offset, 0.5)),
    half: { x: lengths[0] / 2, y: lengths[1] / 2, z: vec.length(slab.offset) / 2 },
    axes: [axis0, axis1, axis2],
  };
}

function projectAabb(half: Vec3, n: Vec3): number {
  return half.x * Math.abs(n.x) + half.y * Math.abs(n.y) + half.z * Math.abs(n.z);
}

function projectObb(obb: Obb, n: Vec3): number {
  return (
    obb.half.x * Math.abs(vec.dot(n, obb.axes[0])) +
    obb.half.y * Math.abs(vec.dot(n, obb.axes[1])) +
    obb.half.z * Math.abs(vec.dot(n, obb.axes[2]))
  );
}

function pushAxis(out: Vec3[], axis: Vec3): void {
  const len = vec.length(axis);
  if (len < AXIS_EPS) return;
  out.push({ x: axis.x / len, y: axis.y / len, z: axis.z / len });
}

function satAxes(obb: Obb): Vec3[] {
  const axes: Vec3[] = [...WORLD_AXES, ...obb.axes];
  for (const world of WORLD_AXES) {
    for (const local of obb.axes) {
      pushAxis(axes, vec.cross(world, local));
    }
  }
  return axes;
}

export function aabbIntersectsObb(box: AabbLike, obb: Obb, epsilon = 0): boolean {
  const center = aabbCenter(box);
  const half = aabbHalf(box);
  const toObb = vec.sub(obb.center, center);
  for (const n of satAxes(obb)) {
    const dist = Math.abs(vec.dot(toObb, n));
    const radius = projectAabb(half, n) + projectObb(obb, n);
    if (dist > radius - epsilon) return false;
  }
  return true;
}

function mtvFromSat(
  toOther: Vec3,
  projectSelf: (n: Vec3) => number,
  projectOther: (n: Vec3) => number,
  axes: readonly Vec3[],
  epsilon: number
): Mtv | null {
  let minDepth = Infinity;
  let best: Vec3 | null = null;
  let sign = 1;
  for (const n of axes) {
    const dist = vec.dot(toOther, n);
    const depth = projectSelf(n) + projectOther(n) - Math.abs(dist);
    if (depth <= epsilon) return null;
    if (depth < minDepth) {
      minDepth = depth;
      best = n;
      sign = dist >= 0 ? -1 : 1;
    }
  }
  if (!best) return null;
  const normal = vec.scale(best, sign);
  return { offset: vec.scale(normal, minDepth), normal, depth: minDepth };
}

export function aabbMtvAabb(a: AabbLike, b: AabbLike, epsilon = 0): Mtv | null {
  const centerA = aabbCenter(a);
  const centerB = aabbCenter(b);
  return mtvFromSat(
    vec.sub(centerB, centerA),
    (n) => projectAabb(aabbHalf(a), n),
    (n) => projectAabb(aabbHalf(b), n),
    WORLD_AXES,
    epsilon
  );
}

export function aabbMtvObb(box: AabbLike, obb: Obb, epsilon = 0): Mtv | null {
  const center = aabbCenter(box);
  return mtvFromSat(
    vec.sub(obb.center, center),
    (n) => projectAabb(aabbHalf(box), n),
    (n) => projectObb(obb, n),
    satAxes(obb),
    epsilon
  );
}

function sweepSat(
  toOther: Vec3,
  projectSelf: (n: Vec3) => number,
  projectOther: (n: Vec3) => number,
  axes: readonly Vec3[],
  delta: Vec3
): SweepHit | null {
  let tEnter = -Infinity;
  let tExit = Infinity;
  let hitAxis: Vec3 | null = null;
  let hitV = 0;

  for (const n of axes) {
    const d0 = vec.dot(toOther, n);
    const radius = projectSelf(n) + projectOther(n);
    const vel = vec.dot(delta, n);

    if (Math.abs(vel) < AXIS_EPS) {
      if (Math.abs(d0) > radius) return null;
      continue;
    }

    const t1 = (d0 - radius) / vel;
    const t2 = (d0 + radius) / vel;
    const tn = Math.min(t1, t2);
    const tf = Math.max(t1, t2);
    if (tn > tEnter) {
      tEnter = tn;
      hitAxis = n;
      hitV = vel;
    }
    if (tf < tExit) tExit = tf;
    if (tEnter > tExit) return null;
  }

  if (!hitAxis || tExit < 0 || tEnter > 1 || tEnter < 0) return null;
  const sign = hitV >= 0 ? -1 : 1;
  return {
    fraction: tEnter,
    normal: { x: hitAxis.x * sign, y: hitAxis.y * sign, z: hitAxis.z * sign },
  };
}

export function sweepAabbAgainstAabb(
  box: AabbLike,
  delta: Vec3,
  other: AabbLike
): SweepHit | null {
  return sweepSat(
    vec.sub(aabbCenter(other), aabbCenter(box)),
    (n) => projectAabb(aabbHalf(box), n),
    (n) => projectAabb(aabbHalf(other), n),
    WORLD_AXES,
    delta
  );
}

export function sweepAabbAgainstObb(box: AabbLike, delta: Vec3, obb: Obb): SweepHit | null {
  return sweepSat(
    vec.sub(obb.center, aabbCenter(box)),
    (n) => projectAabb(aabbHalf(box), n),
    (n) => projectObb(obb, n),
    satAxes(obb),
    delta
  );
}

export function sweepAabbAgainstPlaneY(
  box: AabbLike,
  delta: Vec3,
  planeY: number
): SweepHit | null {
  if (delta.y >= 0) return null;
  const feet = box.min.y;
  const t = (planeY - feet) / delta.y;
  if (t < 0 || t > 1) return null;
  return { fraction: t, normal: { x: 0, y: 1, z: 0 } };
}

function projectInterval(points: readonly Vec3[], n: Vec3): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const point of points) {
    const d = vec.dot(point, n);
    if (d < min) min = d;
    if (d > max) max = d;
  }
  return { min, max };
}

function aabbInterval(box: AabbLike, n: Vec3): { min: number; max: number } {
  const mid = vec.dot(aabbCenter(box), n);
  const radius = projectAabb(aabbHalf(box), n);
  return { min: mid - radius, max: mid + radius };
}

function intervalsOverlap(a: { min: number; max: number }, b: { min: number; max: number }, epsilon: number): boolean {
  return a.max - b.min > epsilon && b.max - a.min > epsilon;
}

function prismSatAxes(prism: TrianglePrism): Vec3[] {
  const ab = vec.sub(prism.b, prism.a);
  const bc = vec.sub(prism.c, prism.b);
  const ca = vec.sub(prism.a, prism.c);
  const ac = vec.sub(prism.c, prism.a);
  const axes: Vec3[] = [...WORLD_AXES];
  pushAxis(axes, vec.cross(ab, ac));
  pushAxis(axes, vec.cross(ab, prism.offset));
  pushAxis(axes, vec.cross(bc, prism.offset));
  pushAxis(axes, vec.cross(ca, prism.offset));
  for (const world of WORLD_AXES) {
    pushAxis(axes, vec.cross(world, ab));
    pushAxis(axes, vec.cross(world, bc));
    pushAxis(axes, vec.cross(world, ca));
    pushAxis(axes, vec.cross(world, prism.offset));
  }
  return axes;
}

export function aabbIntersectsPrism(box: AabbLike, prism: TrianglePrism, epsilon = 0): boolean {
  const verts = prismVertices(prism);
  for (const n of prismSatAxes(prism)) {
    if (!intervalsOverlap(aabbInterval(box, n), projectInterval(verts, n), epsilon)) return false;
  }
  return true;
}

export function aabbMtvPrism(box: AabbLike, prism: TrianglePrism, epsilon = 0): Mtv | null {
  const verts = prismVertices(prism);
  let minDepth = Infinity;
  let best: Vec3 | null = null;
  let sign = 1;
  for (const n of prismSatAxes(prism)) {
    const a = aabbInterval(box, n);
    const b = projectInterval(verts, n);
    const pushNeg = a.max - b.min;
    const pushPos = b.max - a.min;
    if (pushNeg <= epsilon || pushPos <= epsilon) return null;
    if (pushNeg < minDepth) {
      minDepth = pushNeg;
      best = n;
      sign = -1;
    }
    if (pushPos < minDepth) {
      minDepth = pushPos;
      best = n;
      sign = 1;
    }
  }
  if (!best) return null;
  const normal = vec.scale(best, sign);
  return { offset: vec.scale(normal, minDepth), normal, depth: minDepth };
}

export function sweepAabbAgainstPrism(
  box: AabbLike,
  delta: Vec3,
  prism: TrianglePrism
): SweepHit | null {
  const verts = prismVertices(prism);
  let tEnter = -Infinity;
  let tExit = Infinity;
  let hitAxis: Vec3 | null = null;
  let hitV = 0;

  for (const n of prismSatAxes(prism)) {
    const a = aabbInterval(box, n);
    const b = projectInterval(verts, n);
    const vel = vec.dot(delta, n);
    if (Math.abs(vel) < AXIS_EPS) {
      if (!intervalsOverlap(a, b, 0)) return null;
      continue;
    }
    const t1 = (b.min - a.max) / vel;
    const t2 = (b.max - a.min) / vel;
    const tn = Math.min(t1, t2);
    const tf = Math.max(t1, t2);
    if (tn > tEnter) {
      tEnter = tn;
      hitAxis = n;
      hitV = vel;
    }
    if (tf < tExit) tExit = tf;
    if (tEnter > tExit) return null;
  }

  if (!hitAxis || tExit < 0 || tEnter > 1 || tEnter < 0) return null;
  const sign = hitV >= 0 ? -1 : 1;
  return {
    fraction: tEnter,
    normal: { x: hitAxis.x * sign, y: hitAxis.y * sign, z: hitAxis.z * sign },
  };
}
